import { useEffect, useMemo, useState } from "react";
import { Target } from "lucide-react";
import { supabase } from "../lib/supabase";
import { aggregateCampaigns, totalsByWindow, type NiaraDay, type CampaignDailyRow, type DailySnapshotLite, type WindowTotals } from "../lib/aggregations";
import { fmtBRL0, fmtNum, CHANNEL_LABEL, CHANNEL_COLOR } from "../lib/format";
import RoasBadge from "../components/RoasBadge";
import PageHeader from "../components/ui/PageHeader";
import Card from "../components/ui/Card";

const CHANNELS: Array<"meta" | "google" | "tiktok" | "all"> = ["all", "meta", "google", "tiktok"];

async function fetchAllPages<T>(table: string, since: string, channel?: string): Promise<T[]> {
  const PAGE = 1000;
  const out: T[] = [];
  let from = 0;
  while (true) {
    let q = supabase.from(table).select("*").gte("date", since).order("date", { ascending: false }).range(from, from + PAGE - 1);
    if (channel) q = q.eq("channel", channel);
    const { data, error } = await q;
    if (error) break;
    const rows = (data as T[]) ?? [];
    out.push(...rows);
    if (rows.length < PAGE) break;
    from += PAGE;
  }
  return out;
}

export default function Campaigns() {
  const [campRows, setCampRows] = useState<CampaignDailyRow[]>([]);
  const [snaps, setSnaps] = useState<DailySnapshotLite[]>([]);
  const [niara, setNiara] = useState<NiaraDay[]>([]);
  const [loading, setLoading] = useState(true);
  const [channel, setChannel] = useState<typeof CHANNELS[number]>("all");

  useEffect(() => {
    const load = async () => {
      const since = new Date(Date.now() - 31 * 86400_000).toISOString().slice(0, 10);
      const [campData, niaraRes, snapsRes] = await Promise.all([
        fetchAllPages<CampaignDailyRow>("v_ads_campaigns_daily", since),
        supabase.from("v_niara_daily").select("*").gte("date", since).order("date", { ascending: false }),
        supabase.from("ads_daily_snapshots").select("channel,date,account_kpis").gte("date", since),
      ]);
      setCampRows(campData);
      setNiara((niaraRes.data as NiaraDay[]) ?? []);
      const snapsLite: DailySnapshotLite[] = ((snapsRes.data ?? []) as Array<{ channel: string; date: string; account_kpis: { spend_brl?: number; impressions?: number; clicks?: number } }>).map((s) => ({
        channel: s.channel, date: s.date,
        spend_brl: s.account_kpis.spend_brl ?? 0,
        impressions: s.account_kpis.impressions ?? 0,
        clicks: s.account_kpis.clicks ?? 0,
      }));
      setSnaps(snapsLite);
      setLoading(false);
    };
    load();
  }, []);

  const filteredCamp = useMemo(() => channel === "all" ? campRows : campRows.filter((r) => r.channel === channel), [campRows, channel]);
  const filteredSnaps = useMemo(() => channel === "all" ? snaps : snaps.filter((s) => s.channel === channel), [snaps, channel]);
  const { rows, lastDate } = useMemo(() => aggregateCampaigns(filteredCamp, niara), [filteredCamp, niara]);
  // Sempre passa allSnaps pro denominador do share. Quando "all", filteredSnaps == allSnaps → share = 1.
  const totals = useMemo(() => totalsByWindow(filteredSnaps, snaps, niara), [filteredSnaps, snaps, niara]);

  const filterButtons = (
    <div className="flex gap-1 bg-white border border-esm-gray-100 rounded-xl p-1 shadow-card">
      {CHANNELS.map((c) => (
        <button
          key={c}
          onClick={() => setChannel(c)}
          className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${channel === c ? "bg-esm-brown-500 text-white" : "text-esm-gray-700 hover:bg-esm-cream-50"}`}
        >
          {c === "all" ? "Todos" : CHANNEL_LABEL[c]}
        </button>
      ))}
    </div>
  );

  return (
    <>
      <PageHeader
        title="Campanhas"
        subtitle={`Última coleta: ${lastDate ?? "—"} · ROAS pelo motor Niara`}
        icon={<Target size={22} strokeWidth={2} />}
        actions={filterButtons}
      />

      {channel === "tiktok" && (
        <div className="mb-4 flex items-start gap-3 bg-esm-cream-50 border border-esm-brown-100 rounded-2xl p-4 text-[12px] text-esm-brown-900">
          <span className="text-base leading-none mt-0.5">📣</span>
          <div>
            <p className="font-bold mb-0.5">TikTok é topo de funil (awareness)</p>
            <p className="leading-relaxed">
              Aqui medimos <b>alcance e engajamento</b> (impressões, cliques, CTR, CPM) — não ROAS.
              O TikTok gera demanda que se converte depois por outros canais, então atribuir
              receita/reservas direto a ele (rateio) infla um retorno que não é real.
            </p>
          </div>
        </div>
      )}

      <div className="space-y-3 mb-6">
        <KpiRow label="Ontem (24h)"   tone="primary"   totals={totals.d24}  tofu={channel === "tiktok"} />
        <KpiRow label="Média 3 dias"  tone="secondary" totals={totals.mm3}  tofu={channel === "tiktok"} />
        <KpiRow label="Média 7 dias"  tone="secondary" totals={totals.mm7}  tofu={channel === "tiktok"} />
        <KpiRow label="Média 15 dias" tone="tertiary"  totals={totals.mm15} tofu={channel === "tiktok"} />
        <KpiRow label="Média 30 dias" tone="tertiary"  totals={totals.d30}  tofu={channel === "tiktok"} />
      </div>

      <Card
        title={`Campanhas ativas — ${rows.length}`}
        subtitle="Com gasto nos últimos 30 dias · ROAS por janela 24h / 3d / 7d"
        padded={false}
        actions={
          <div className="flex items-center gap-3 text-[11px] text-esm-gray-400">
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#22c55e]"/> ≥3x</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-esm-brown-300"/> ≥2x</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#f59e0b]"/> ≥1x</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#ef4444]"/> &lt;1x</span>
          </div>
        }
      >
        {loading ? (
          <div className="p-12 text-center text-esm-gray-400 italic">Carregando…</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                {/* Linha 1: grupos */}
                <tr className="text-left text-esm-gray-400 text-[9px] uppercase tracking-wider bg-esm-cream-50">
                  <th className="p-2"></th>
                  <th className="p-2"></th>
                  <th className="p-2"></th>
                  <th className="p-2"></th>
                  <th className="p-2 pt-3 text-center font-bold border-l border-esm-gray-100" colSpan={3}>Gasto</th>
                  <th className="p-2 pt-3 text-center font-bold border-l border-esm-gray-100" colSpan={3}>Receita</th>
                  <th className="p-2 pt-3 text-center font-bold border-l border-esm-gray-100" colSpan={3}>ROAS</th>
                  <th className="p-2"></th>
                </tr>
                {/* Linha 2: colunas */}
                <tr className="text-left text-esm-gray-500 text-[11px] uppercase tracking-wider bg-esm-cream-50">
                  <th className="p-3 pt-1"></th>
                  <th className="p-3 pt-1">Campanha</th>
                  <th className="p-3 pt-1 text-right">Compras 24h</th>
                  <th className="p-3 pt-1 text-right">AOV</th>
                  <th className="p-3 pt-1 text-right border-l border-esm-gray-100">24h</th>
                  <th className="p-3 pt-1 text-right">3d</th>
                  <th className="p-3 pt-1 text-right">7d</th>
                  <th className="p-3 pt-1 text-right border-l border-esm-gray-100">24h</th>
                  <th className="p-3 pt-1 text-right">3d</th>
                  <th className="p-3 pt-1 text-right">7d</th>
                  <th className="p-3 pt-1 text-right border-l border-esm-gray-100">24h</th>
                  <th className="p-3 pt-1 text-right">3d</th>
                  <th className="p-3 pt-1 text-right">7d</th>
                  <th className="p-3 pt-1 text-right">Tendência</th>
                </tr>
              </thead>
              {/* Agrupa por canal — header de seção destacado pra cada bloco */}
              {(() => {
                if (rows.length === 0 && !loading) {
                  return (
                    <tbody>
                      <tr><td colSpan={14} className="p-10 text-center text-esm-gray-400 italic">Sem campanhas com gasto nesse filtro.</td></tr>
                    </tbody>
                  );
                }
                const byChannel = new Map<string, typeof rows>();
                for (const r of rows) {
                  if (!byChannel.has(r.channel)) byChannel.set(r.channel, []);
                  byChannel.get(r.channel)!.push(r);
                }
                // Ordena os grupos pelo gasto total 30d desc
                const groups = [...byChannel.entries()].sort((a, b) => {
                  const sa = a[1].reduce((s, r) => s + r.spend_30d, 0);
                  const sb = b[1].reduce((s, r) => s + r.spend_30d, 0);
                  return sb - sa;
                });
                return groups.map(([ch, list]) => {
                  const spend30 = list.reduce((s, r) => s + r.spend_30d, 0);
                  const rev30   = list.reduce((s, r) => s + r.revenue_30d, 0);
                  const purch30 = list.reduce((s, r) => s + r.purchases_30d, 0);
                  const roas30  = spend30 > 0 ? rev30 / spend30 : 0;
                  const color   = CHANNEL_COLOR[ch];
                  return (
                    <tbody key={ch}>
                      {/* Header do grupo do canal */}
                      <tr className="border-t-2" style={{ borderTopColor: color }}>
                        <td colSpan={14} className="p-0">
                          <div className="flex items-center justify-between gap-4 px-3 py-2.5" style={{ background: `${color}10` }}>
                            <div className="flex items-center gap-2.5">
                              <span className="w-3 h-3 rounded-full shadow-sm" style={{ background: color }} />
                              <span className="text-[13px] font-bold text-esm-gray-900 uppercase tracking-wider">{CHANNEL_LABEL[ch]}</span>
                              <span className="text-[11px] text-esm-gray-500 font-semibold">{list.length} campanha{list.length === 1 ? "" : "s"}</span>
                            </div>
                            <div className="flex items-center gap-5 text-[11px] tabular-nums">
                              <span className="text-esm-gray-500"><b className="text-esm-gray-900">{fmtBRL0(spend30)}</b> gasto 30d</span>
                              {ch === "tiktok" ? (
                                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md bg-esm-brown-100 text-esm-brown-700">TOFU · topo de funil</span>
                              ) : (
                                <>
                                  <span className="text-esm-gray-500"><b className="text-esm-brown-700">{fmtBRL0(rev30)}</b> receita 30d</span>
                                  <span className="text-esm-gray-500"><b className="text-esm-gray-900">{fmtNum(Math.round(purch30))}</b> compras 30d</span>
                                  <RoasBadge roas={roas30} size="md" />
                                </>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                      {/* Rows do canal */}
                      {list.map((r) => {
                        const aov = r.purchases_24h > 0 ? r.revenue_24h / r.purchases_24h : 0;
                        const isTofu = r.channel === "tiktok";
                        const dash = <span className="text-esm-gray-300">—</span>;
                        return (
                          <tr key={`${r.channel}-${r.campaign_id}`} className="border-t border-esm-gray-100 hover:bg-esm-cream-50/40">
                            <td className="p-3 pl-5">
                              <span className="w-1 h-10 inline-block rounded" style={{ background: color }} />
                            </td>
                            <td className="p-3">
                              <p className="font-bold text-esm-gray-900 leading-tight">{r.campaign_name}</p>
                              <p className="text-[10px] text-esm-gray-400 mt-0.5">{r.objective || "—"} · <code className="text-[10px]">{r.campaign_id}</code></p>
                            </td>
                            <td className="p-3 text-right tabular-nums">{isTofu ? dash : (r.purchases_24h >= 0.1 ? r.purchases_24h.toFixed(1).replace(".", ",") : "—")}</td>
                            <td className="p-3 text-right tabular-nums">{isTofu ? dash : (aov > 0 ? fmtBRL0(aov) : "—")}</td>
                            {/* Gasto */}
                            <td className="p-3 text-right tabular-nums font-semibold text-esm-gray-900 border-l border-esm-gray-100">{fmtBRL0(r.spend_24h)}</td>
                            <td className="p-3 text-right tabular-nums text-esm-gray-700">{fmtBRL0(r.spend_3d)}</td>
                            <td className="p-3 text-right tabular-nums text-esm-gray-700">{fmtBRL0(r.spend_7d)}</td>
                            {/* Receita (TikTok = TOFU, sem receita atribuída) */}
                            <td className="p-3 text-right tabular-nums font-semibold text-esm-brown-700 border-l border-esm-gray-100">{isTofu ? dash : fmtBRL0(r.revenue_24h)}</td>
                            <td className="p-3 text-right tabular-nums text-esm-gray-700">{isTofu ? dash : fmtBRL0(r.revenue_3d)}</td>
                            <td className="p-3 text-right tabular-nums text-esm-gray-700">{isTofu ? dash : fmtBRL0(r.revenue_7d)}</td>
                            {/* ROAS (TikTok = TOFU, não se mede por ROAS) */}
                            <td className="p-3 text-right border-l border-esm-gray-100">{isTofu ? dash : <RoasBadge roas={r.roas_24h} reference={r.roas_7d} />}</td>
                            <td className="p-3 text-right">{isTofu ? dash : <RoasBadge roas={r.roas_3d} />}</td>
                            <td className="p-3 text-right">{isTofu ? dash : <RoasBadge roas={r.roas_7d} />}</td>
                            <td className="p-3 text-right pr-5">{isTofu ? dash : <TrendChip trend={r.trend} />}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  );
                });
              })()}
            </table>
          </div>
        )}
      </Card>
    </>
  );
}

function KpiRow({ label, totals, tone, tofu = false }: { label: string; totals: WindowTotals; tone: "primary" | "secondary" | "tertiary"; tofu?: boolean }) {
  const cfg: Record<string, { container: string; valueSize: string; labelClass: string; metaClass: string }> = {
    primary:   { container: "bg-gradient-to-r from-esm-brown-700 to-esm-brown-900 text-white shadow-card-hover", valueSize: "text-3xl",     labelClass: "text-[10px] text-esm-cream-100/80", metaClass: "text-esm-cream-100/80" },
    secondary: { container: "bg-white border border-esm-gray-100 shadow-card",                                    valueSize: "text-2xl text-esm-gray-900", labelClass: "text-[10px] text-esm-gray-400", metaClass: "text-esm-gray-400" },
    tertiary:  { container: "bg-esm-cream-50 border border-esm-brown-100 shadow-card",                            valueSize: "text-xl text-esm-gray-900",  labelClass: "text-[10px] text-esm-brown-700", metaClass: "text-esm-brown-700/70" },
  };
  const s = cfg[tone]!;
  const meta = tofu
    ? (totals.spend > 0 ? `${fmtBRL0(totals.spend)} gasto · ${fmtNum(Math.round(totals.impressions))} impressões` : "sem dados")
    : (totals.spend > 0 ? `${fmtBRL0(totals.spend)} gasto · ${fmtNum(Math.round(totals.purchases))} compras` : "sem dados");
  return (
    <div className={`rounded-2xl px-5 py-4 ${s.container}`}>
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <span className={`text-[11px] font-bold uppercase tracking-wider ${tone === "primary" ? "text-esm-cream-100" : "text-esm-gray-700"}`}>{label}</span>
        <span className={`text-[10px] ${s.metaClass}`}>{meta}</span>
      </div>
      {tofu ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-x-6 gap-y-2">
          <KpiCell label="Gasto"      value={fmtBRL0(totals.spend)} sizeClass={s.valueSize} labelClass={s.labelClass} />
          <KpiCell label="Impressões" value={totals.impressions > 0 ? fmtNum(Math.round(totals.impressions)) : "—"} sizeClass={s.valueSize} labelClass={s.labelClass} />
          <KpiCell label="Cliques"    value={totals.clicks > 0 ? fmtNum(Math.round(totals.clicks)) : "—"} sizeClass={s.valueSize} labelClass={s.labelClass} />
          <KpiCell label="CTR"        value={totals.ctr > 0 ? `${totals.ctr.toFixed(2).replace(".", ",")}%` : "—"} sizeClass={s.valueSize} labelClass={s.labelClass} />
          <KpiCell label="CPM"        value={totals.cpm > 0 ? fmtBRL0(totals.cpm) : "—"} sizeClass={s.valueSize} labelClass={s.labelClass} />
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-x-6 gap-y-2">
          <KpiCell label="ROAS"    value={totals.roas > 0 ? `${totals.roas.toFixed(2).replace(".", ",")}x` : "—"} sizeClass={s.valueSize} labelClass={s.labelClass} />
          <KpiCell label="Receita" value={fmtBRL0(totals.revenue)} sizeClass={s.valueSize} labelClass={s.labelClass} />
          <KpiCell label="Gasto"   value={fmtBRL0(totals.spend)} sizeClass={s.valueSize} labelClass={s.labelClass} />
          <KpiCell label="CPA"     value={totals.cpa > 0 ? fmtBRL0(totals.cpa) : "—"} sizeClass={s.valueSize} labelClass={s.labelClass} />
          <KpiCell label="Compras" value={totals.purchases >= 0.1 ? totals.purchases.toFixed(1).replace(".", ",") : "—"} sizeClass={s.valueSize} labelClass={s.labelClass} />
        </div>
      )}
    </div>
  );
}

function KpiCell({ label, value, sizeClass, labelClass }: { label: string; value: string; sizeClass: string; labelClass: string }) {
  return (
    <div>
      <p className={`uppercase tracking-wider font-bold ${labelClass}`}>{label}</p>
      <p className={`font-bold tabular-nums mt-1 leading-none ${sizeClass}`}>{value}</p>
    </div>
  );
}

function TrendChip({ trend }: { trend: "up" | "down" | "stable" }) {
  const map = {
    up:     { label: "Subindo", cls: "bg-[#dcfce7] text-[#166534]", icon: "↗" },
    down:   { label: "Caindo",  cls: "bg-[#fee2e2] text-[#991b1b]", icon: "↘" },
    stable: { label: "Estável", cls: "bg-esm-gray-100 text-esm-gray-700", icon: "→" },
  } as const;
  const t = map[trend];
  return <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-md ${t.cls}`}>{t.icon} {t.label}</span>;
}
